import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { fetchShowPublishment } from "../../features/publishments/ShowPublishment";
import { useTranslation } from "react-i18next";
import { t } from "i18next";

function ShowPublishment() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();
  useEffect(() => {
    dispatch(fetchShowPublishment(id));
  }, [dispatch, id, i18n.language]);

  const { publishment } = useSelector((state) => state.showPublishment);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, when: "beforeChildren" }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 120 }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { type: "spring", stiffness: 120, delay: 0.2 }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex justify-center flex-col items-center p-6"
    >
      <motion.h1
        className="text-2xl font-bold mb-4"
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200 }}
      >
        {publishment?.main_title}
      </motion.h1>

      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 justify-items-center gap-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Image Section */}
        <AnimatePresence>
          {publishment?.image && (
            <motion.div
              variants={imageVariants}
              className="w-full max-w-md relative"
              whileHover={{ scale: 1.05 }}
            >
              <img
                src={process.env.REACT_APP_IMG_URL + publishment.image}
                alt="Brochure"
                className="w-full h-auto rounded-lg shadow-lg transform transition-all duration-300 hover:shadow-xl"
              />
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                className="absolute bottom-0 h-1 bg-green-600 rounded-full"
                transition={{ duration: 0.8 }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Details Section */}
        <motion.div
          className="flex flex-col gap-6 w-full max-w-md"
          variants={containerVariants}
        >
          {/* Title */}
          <motion.div
            variants={itemVariants}
            className="flex items-start gap-2 flex-col"
            whileHover={{ x: 10 }}
          >
            <motion.span
              className="bg-green-600 text-white font-semibold px-4 py-2 rounded-md shadow-lg"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
            >
              {t("title")}
            </motion.span>
            <motion.p
              className="text-gray-700 text-lg p-2   w-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {publishment?.title}
            </motion.p>
          </motion.div>

          {/* Description */}
          {
            publishment?.description && (<motion.div
              variants={itemVariants}
              className="flex items-start gap-2 flex-col"
              whileHover={{ x: 10 }}
            >
              <motion.span
                className="bg-green-600 text-white font-semibold px-4 py-2 rounded-md shadow-lg"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
              >
                {t("description")}
              </motion.span>
              <motion.p
                className="text-gray-700 text-lg p-2  w-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                {publishment?.description}
              </motion.p>
            </motion.div>)
          }

        </motion.div>
      </motion.div>

      {/* Content Box */}
      {
        publishment?.content && (<motion.div
          className="border-2 border-gray-300 shadow-xl rounded-lg p-6 mt-8 w-full max-w-xl  "
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 100, delay: 0.5 }}
        >
          <motion.p
            className="font-semibold text-green-600 mb-3 text-lg"
            whileHover={{ scale: 1.02 }}
          >
            {t("content")}
          </motion.p>
          <motion.p
            className="text-gray-700 leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            {publishment?.content}
          </motion.p>
        </motion.div>)
      }


      {/* Download PDF Button */}
      {
        publishment?.attachment && (<AnimatePresence>
          {publishment?.attachment && (
            <motion.a
              href={process.env.REACT_APP_IMG_URL + publishment.attachment}
              download
              className="mt-8 bg-green-600 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:bg-green-700 w-11/12  md:w-4/12 text-center justify-center transition-colors flex items-center gap-2"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              target="_blank"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              {t("download")}
            </motion.a>
          )}
        </AnimatePresence>)
      }

      {
        publishment?.link && ( <div className="relative mt-5 w-full max-w-4xl mx-auto overflow-hidden rounded-2xl border border-gray-200 shadow-lg bg-gray-100 transition-all duration-300 hover:shadow-2xl">
        <div className="relative pt-[56.25%]"> {/* 16:9 Aspect Ratio */}
          <iframe
            src={publishment?.link}
            className="absolute top-0 left-0 w-full h-full rounded-2xl"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </div>)
      }
     
    </motion.div>
  );
}

export default ShowPublishment;